/**
 * A live meditation room. Contract twin of
 * `api/src/models/live-meditation-session.model.ts` (no codegen — keep in sync).
 *
 * ONE entity for every live room: a cohort's coach-led call, an ad-hoc admin
 * session, and a room a member creates for their own connections. Staff create
 * the coach-led ones here and can pull any of them.
 */
export type LiveSessionStatus = 'draft' | 'scheduled' | 'live' | 'ended' | 'cancelled';
export type LiveSessionFormat = '1:1' | 'small-group' | 'coach-led';
export type LiveSessionMeditationType = 'breathing' | 'body-scan' | 'silent' | 'guided';
export type RoomPrivacy = 'public' | 'private';

/**
 * What the host allows PARTICIPANTS to do. The host and admins are always exempt.
 * `disabled` is what "participants cannot speak" means.
 */
export type MediaPolicy = 'free' | 'on-request' | 'disabled';

export interface LiveRoomSettings {
    maxParticipants: number;
    audioPolicy: MediaPolicy;
    videoPolicy: MediaPolicy;
    participantJoinMuted: boolean;
    participantJoinCameraOff: boolean;
    reactionsEnabled: boolean;
    chatEnabled: boolean;
    raiseHandEnabled: boolean;
    waitingRoom: boolean;
}

/** When a recurring ANCHOR session re-opens. Wall-clock in `timezone`. */
export interface LiveSessionRecurrence {
    frequency: 'daily' | 'weekly';
    timeOfDay: string;
    timezone: string;
    daysOfWeek?: number[];
    active: boolean;
}

export interface LiveMeditationSession {
    id: string;
    created_at: string;
    updated_at: string;
    deleted?: boolean;
    title: string;
    description?: string;
    status: LiveSessionStatus;
    format: LiveSessionFormat;
    meditationType: LiveSessionMeditationType;
    contentId?: string;
    /** Whoever RUNS the room. Host powers are `Admin OR uid === hostUserId`. */
    hostUserId: string;
    hostName: string;
    durationMinutes: number;
    privacy: RoomPrivacy;
    inviteCode: string;
    accessUserIds: string[];
    settings: LiveRoomSettings;
    scheduledAt?: string | null;
    startedAt?: string | null;
    endsAt?: string | null;
    endedAt?: string | null;
    followerUserIds: string[];
    /** Set on a recurring TEMPLATE — it spawns dated occurrences, and is never joined itself. */
    recurrence?: LiveSessionRecurrence | null;
    recurrenceParentId?: string | null;
}

export interface Coach {
    id: string;
    name: string;
    email: string;
}

/**
 * The session's status as of now, DERIVED. The stored `status` is a lagging
 * cache a sweep tidies up, so a table that read it would show sessions as live
 * minutes after they finished. Mirrors `deriveLiveSessionStatus` in the API.
 */
export const deriveLiveSessionStatus = (session: LiveMeditationSession, now: Date = new Date()): LiveSessionStatus => {
    if (session.status === 'cancelled') return 'cancelled';
    if (session.status === 'draft') return 'draft';
    if (session.status === 'ended') return 'ended';
    const nowMs = now.getTime();
    if (session.endsAt && nowMs >= new Date(session.endsAt).getTime()) return 'ended';
    if (session.startedAt && nowMs >= new Date(session.startedAt).getTime()) return 'live';
    if (session.scheduledAt && nowMs >= new Date(session.scheduledAt).getTime()) return 'live';
    return 'scheduled';
};

export const MEDITATION_TYPE_LABELS: Record<LiveSessionMeditationType, string> = {
    breathing: 'Breathing',
    'body-scan': 'Body scan',
    silent: 'Silent',
    guided: 'Guided track',
};

export const MEDIA_POLICY_LABELS: Record<MediaPolicy, string> = {
    free: 'Free — they control it',
    'on-request': 'On request — raise hand, host grants',
    disabled: 'Off — they cannot turn it on',
};

export default LiveMeditationSession;
